/**
 * CompanionTicketCard - Turno de un acompañante, visto por quien lo reservó.
 *
 * Cuando alguien pide turno para sí y para acompañantes, se crea un documento en
 * `queues` por cada uno: todos con el MISMO `userId` (para que las notificaciones
 * lleguen a quien reservó) y los del acompañante marcados con `isCompanion`,
 * `companionName` y `parentQueueId`.
 *
 * Hasta ahora esos turnos no se pintaban en ninguna pantalla del cliente, así que
 * quien venía con un acompañante no podía ver ni con qué barbero le habían puesto
 * ni en qué posición estaba. Sólo aparecían en la vista del barbero.
 *
 * La tarjeta es de sólo lectura a propósito: cancelar o marcar llegada actúa sobre
 * un turno concreto y no está resuelto qué debe pasar con el turno principal
 * cuando se cancela el del acompañante (ni al revés). Mejor no ofrecer un botón
 * cuyo efecto no está definido.
 */

import { useTranslation } from 'react-i18next';
import { Users, Clock } from 'lucide-react';
import type { QueueTicket } from '../../../types';
import { getStatusBadge, getStatusText } from './statusHelpers';

interface CompanionTicketCardProps {
  tickets: QueueTicket[];
}

export function CompanionTicketCard({ tickets }: CompanionTicketCardProps) {
  const { t } = useTranslation();

  if (tickets.length === 0) return null;

  return (
    <div className="bg-surface border border-hairline rounded-xl p-6 mb-6">
      <h2 className="font-sans text-base font-semibold tracking-tight text-ink mb-1 inline-flex items-center gap-2">
        <Users className="w-4 h-4 text-ink-subtle" aria-hidden="true" />
        {tickets.length === 1 ? 'Tu acompañante' : 'Tus acompañantes'}
      </h2>
      <p className="text-sm text-ink-muted mb-4">
        {tickets.length === 1
          ? 'Tiene su propio turno, que puede ir por separado del tuyo.'
          : 'Cada uno tiene su propio turno, que puede ir por separado del tuyo.'}
      </p>

      <div className="space-y-3">
        {tickets.map((ticket) => (
          <div
            key={ticket.queueId}
            className="p-4 bg-surface-2 border border-hairline rounded-lg"
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="font-semibold text-ink">
                  {ticket.companionName || 'Acompañante'}
                </p>
                {ticket.serviceName && (
                  <p className="text-sm text-ink-muted mt-0.5">{ticket.serviceName}</p>
                )}
                {ticket.barberName && (
                  <p className="text-sm text-ink-muted mt-0.5">
                    Barbero: {ticket.barberName}
                  </p>
                )}
              </div>

              <div className="text-right">
                <span
                  className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(ticket.status)}`}
                >
                  {getStatusText(ticket.status, t)}
                </span>
                {typeof ticket.position === 'number' && ticket.position > 0 && (
                  <p className="text-sm text-ink-muted mt-2 tabular-nums">
                    Posición {ticket.position}
                  </p>
                )}
                {/* El acompañante puede estar con OTRO barbero, así que su espera
                    no se parece a la del turno principal y la posición por sí sola
                    no dice nada comparable: "posición 2" en otra cola puede ser
                    más tarde que "posición 4" en la tuya. Se muestra el estimado
                    que calcula el backend en el propio turno, el mismo que usan las
                    notificaciones. */}
                {ticket.status === 'waiting' && ticket.estimatedWaitTime > 0 && (
                  <p className="inline-flex items-center gap-1 text-sm text-ink-muted mt-1 tabular-nums">
                    <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                    ~{ticket.estimatedWaitTime} min
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
